import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { auth, db, studioDb } from "../firebase";

function toMillis(value) {
  return (
    value?.toMillis?.() ||
    (typeof value?.seconds === "number" ? value.seconds * 1000 : 0)
  );
}

function isCapsuleActive(capsule = {}) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const revealDate = capsule?.revealDate?.toDate?.() || null;
  if (revealDate && revealDate > now) return false;

  const endDate = capsule?.endDate?.toDate?.() || null;
  if (endDate) {
    const normalizedEnd = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate()
    );
    if (normalizedEnd < today) return false;
  }

  return true;
}

function normalizeBusinessName(business = {}) {
  return String(
    business?.displayName ||
    business?.companyName ||
    business?.name ||
    ""
  ).trim();
}

function normalizeBusinessAvatar(business = {}) {
  return String(
    business?.avatarUrl ||
    business?.logoUrl ||
    business?.profileImage ||
    ""
  ).trim();
}

export async function fetchBusinessById(businessId) {
  const normalizedBusinessId = String(businessId || "").trim();
  if (!normalizedBusinessId || !studioDb) return null;

  const ref = doc(studioDb, "businesses", normalizedBusinessId);
  const snapshot = await getDoc(ref);
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() };
}

export async function fetchBusinessesByIds(businessIds = []) {
  const ids = Array.from(
    new Set(
      (Array.isArray(businessIds) ? businessIds : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    )
  );
  if (!ids.length || !studioDb) return {};

  const entries = await Promise.all(
    ids.map(async (businessId) => [businessId, await fetchBusinessById(businessId)])
  );

  return entries.reduce((acc, [businessId, business]) => {
    acc[businessId] = business;
    return acc;
  }, {});
}

export function subscribeFollowingBusinesses(userId, callback, onError) {
  const uid = String(userId || "").trim();
  if (!uid) return () => {};

  const ref = collection(db, "users", uid, "followingBusinesses");
  return onSnapshot(
    ref,
    (snapshot) => {
      const rows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      callback(rows);
    },
    (error) => {
      console.error("Failed to subscribe to followed businesses:", error);
      callback([]);
      if (typeof onError === "function") {
        onError(error);
      }
    }
  );
}

export async function isFollowingBusiness(userId, businessId) {
  const uid = String(userId || "").trim();
  const normalizedBusinessId = String(businessId || "").trim();
  if (!uid || !normalizedBusinessId) return false;

  const ref = doc(db, "users", uid, "followingBusinesses", normalizedBusinessId);
  const snapshot = await getDoc(ref);
  return snapshot.exists();
}

export async function followBusiness(userId, business = {}) {
  const uid = String(userId || "").trim();
  const businessId = String(business?.id || "").trim();
  if (!uid || !businessId) {
    throw new Error("User ID and business ID are required to follow a business.");
  }

  const payload = {
    businessId,
    businessName: normalizeBusinessName(business),
    businessAvatarUrl: normalizeBusinessAvatar(business),
    followedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(doc(db, "users", uid, "followingBusinesses", businessId), payload, { merge: true });

  if (studioDb) {
    const followerPayload = {
      uid,
      followedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    try {
      await setDoc(
        doc(studioDb, "businesses", businessId, "followers", uid),
        followerPayload,
        { merge: true }
      );
    } catch {
      // Keep follow action resilient even if studio follower mirror is restricted.
    }
  }
}

export async function unfollowBusiness(userId, businessId) {
  const uid = String(userId || "").trim();
  const normalizedBusinessId = String(businessId || "").trim();
  if (!uid || !normalizedBusinessId) return;

  await deleteDoc(doc(db, "users", uid, "followingBusinesses", normalizedBusinessId));

  if (studioDb) {
    try {
      await deleteDoc(doc(studioDb, "businesses", normalizedBusinessId, "followers", uid));
    } catch {
      // Keep unfollow resilient if followers subcollection rules differ.
    }
  }
}

export async function fetchBusinessFollowersCount(businessId) {
  const normalizedBusinessId = String(businessId || "").trim();
  if (!normalizedBusinessId) return 0;

  if (studioDb) {
    try {
      const studioFollowers = await getDocs(
        collection(studioDb, "businesses", normalizedBusinessId, "followers")
      );
      return studioFollowers.size;
    } catch {
      // Fall through to default-db aggregate query below.
    }
  }

  try {
    const followDocs = await getDocs(
      query(
        collectionGroup(db, "followingBusinesses"),
        where("businessId", "==", normalizedBusinessId)
      )
    );
    return followDocs.size;
  } catch {
    return 0;
  }
}

export async function fetchBusinessSponsoredCapsules(businessId, { limit = 25 } = {}) {
  const normalizedBusinessId = String(businessId || "").trim();
  if (!normalizedBusinessId) return [];

  const [byBusinessIdSnap, bySenderIdSnap] = await Promise.all([
    getDocs(
      query(
        collection(db, "timeCapsules"),
        where("capsuleType", "==", "sponsored"),
        where("status", "==", "published"),
        where("businessId", "==", normalizedBusinessId)
      )
    ),
    getDocs(
      query(
        collection(db, "timeCapsules"),
        where("capsuleType", "==", "sponsored"),
        where("status", "==", "published"),
        where("senderId", "==", normalizedBusinessId)
      )
    ),
  ]);

  const byId = new Map();
  [byBusinessIdSnap, bySenderIdSnap].forEach((snap) => {
    snap.docs.forEach((docSnap) => {
      byId.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
    });
  });

  return Array.from(byId.values())
    .filter(isCapsuleActive)
    .sort((a, b) => {
      const aTs = toMillis(a?.sentAt) || toMillis(a?.createdAt);
      const bTs = toMillis(b?.sentAt) || toMillis(b?.createdAt);
      return bTs - aTs;
    })
    .slice(0, Math.max(1, Number(limit) || 25));
}

export async function fetchSponsoredCapsulesForBusinessIds(
  businessIds = [],
  { limit = 50 } = {}
) {
  const ids = Array.from(
    new Set(
      (Array.isArray(businessIds) ? businessIds : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    )
  );
  if (!ids.length) return [];

  const chunks = [];
  for (let i = 0; i < ids.length; i += 10) {
    chunks.push(ids.slice(i, i + 10));
  }

  const snapshots = await Promise.all(
    chunks.flatMap((chunk) => [
      getDocs(
        query(
          collection(db, "timeCapsules"),
          where("capsuleType", "==", "sponsored"),
          where("status", "==", "published"),
          where("businessId", "in", chunk)
        )
      ),
      getDocs(
        query(
          collection(db, "timeCapsules"),
          where("capsuleType", "==", "sponsored"),
          where("status", "==", "published"),
          where("senderId", "in", chunk)
        )
      ),
    ])
  );

  const byId = new Map();
  snapshots.forEach((snapshot) => {
    snapshot.docs.forEach((docSnap) => {
      byId.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
    });
  });

  return Array.from(byId.values())
    .filter(isCapsuleActive)
    .sort((a, b) => {
      const aTs = toMillis(a?.sentAt) || toMillis(a?.createdAt);
      const bTs = toMillis(b?.sentAt) || toMillis(b?.createdAt);
      return bTs - aTs;
    })
    .slice(0, Math.max(1, Number(limit) || 50));
}

export function subscribeSponsoredCapsulesForBusinessIds(
  businessIds = [],
  callback = () => {},
  { limit = 50 } = {}
) {
  const ids = Array.from(
    new Set(
      (Array.isArray(businessIds) ? businessIds : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    )
  );

  if (!ids.length) {
    callback([]);
    return () => {};
  }

  const chunks = [];
  for (let i = 0; i < ids.length; i += 10) {
    chunks.push(ids.slice(i, i + 10));
  }

  const latestResults = new Map();
  const emit = () => {
    const byId = new Map();
    latestResults.forEach((rows) => {
      rows.forEach((row) => {
        byId.set(row.id, row);
      });
    });

    const merged = Array.from(byId.values())
      .filter(isCapsuleActive)
      .sort((a, b) => {
        const aTs = toMillis(a?.sentAt) || toMillis(a?.createdAt);
        const bTs = toMillis(b?.sentAt) || toMillis(b?.createdAt);
        return bTs - aTs;
      })
      .slice(0, Math.max(1, Number(limit) || 50));

    callback(merged);
  };

  const unsubscribers = chunks.flatMap((chunk, chunkIndex) => {
    const byBusinessIdKey = `businessId-${chunkIndex}`;
    const bySenderIdKey = `senderId-${chunkIndex}`;
    const commonFilters = [
      where("capsuleType", "==", "sponsored"),
      where("status", "==", "published"),
    ];

    const byBusinessUnsub = onSnapshot(
      query(
        collection(db, "timeCapsules"),
        ...commonFilters,
        where("businessId", "in", chunk)
      ),
      (snapshot) => {
        latestResults.set(
          byBusinessIdKey,
          snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        );
        emit();
      },
      (err) => {
        console.error("Failed to subscribe to sponsored capsules by businessId:", err);
        latestResults.set(byBusinessIdKey, []);
        emit();
      }
    );

    const bySenderUnsub = onSnapshot(
      query(
        collection(db, "timeCapsules"),
        ...commonFilters,
        where("senderId", "in", chunk)
      ),
      (snapshot) => {
        latestResults.set(
          bySenderIdKey,
          snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        );
        emit();
      },
      (err) => {
        console.error("Failed to subscribe to sponsored capsules by senderId:", err);
        latestResults.set(bySenderIdKey, []);
        emit();
      }
    );

    return [byBusinessUnsub, bySenderUnsub];
  });

  return () => {
    unsubscribers.forEach((unsubscribe) => {
      if (typeof unsubscribe === "function") unsubscribe();
    });
  };
}

export function getCurrentUserId() {
  return auth.currentUser?.uid || "";
}
